import { Avatar, CollapseCard, ErrorBoundary } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { useEffect, useRef } from 'react';

import { CardMoveHandle } from 'components/conversation/components/sidebar/components/cardMoveHandle/cardMoveHandle';
import { VisibilityToggle } from 'components/conversation/components/sidebar/components/visibilityToggle/visibilityToggle';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useConversationSidebarSettings } from 'utils/useConversationSidebarSettings';
import { UseOrderedDragDropOptions } from 'utils/useOrderedDragDrop';

import {
  setup as setupElevioSnowcat,
  setupUnload as unloadElevioSnowcat,
} from '../../analytics/snowcat';
import { elevioSelectors } from '../../elevio.reducer/selectors';
import { KnowledgeContent } from '../KnowledgeContent';
import { KnowledgeErrorPlaceholder } from '../KnowledgeErrorPlaceholder/KnowledgeErrorPlaceholder';

import { LanguagePickerDropdown } from './ElevioLanguagePickerDropdown';

const avatar = (
  <Avatar
    icon="elevio"
    color={{
      backgroundColor: colors.neutral['200'],
      contentColor: typography.color['tertiary'],
    }}
  />
);

type ElevioCardProps = Pick<
  UseOrderedDragDropOptions,
  'type' | 'index' | 'onMove'
> & {
  isMinimized?: boolean;
};

export function ElevioCard({
  isMinimized,
  index,
  type,
  onMove,
}: ElevioCardProps): JSX.Element {
  const row = useRef<HTMLDivElement>(null);
  const { cardOpenState, setCardOpen } = useConversationSidebarSettings();

  // Set up event tracking for feedback component
  useEffect(() => {
    setupElevioSnowcat();
    return () => unloadElevioSnowcat(); // flush buffer if component unmounts
  }, []);

  const elevioCardOpen = cardOpenState['elevio'] ?? true;
  const languages = useAppSelector(elevioSelectors.suggestion.languages);
  const errorMessage = useAppSelector(elevioSelectors.suggestion.errorMessage);
  const {
    selectedLanguage: { nativeName: selectedLanguageNativeName },
  } = languages;

  if (isMinimized) {
    return <div className="icon-holder">{avatar}</div>;
  }

  const onToggle = (isOpen: boolean): void => {
    setCardOpen('elevio', isOpen);
  };

  return (
    <CollapseCard
      containerRef={row}
      onToggle={onToggle}
      isOpen={elevioCardOpen}
    >
      <CollapseCard.Header
        dropdown={<LanguagePickerDropdown />}
        dropdownTrigger={selectedLanguageNativeName}
        avatar={avatar}
        slotBeforeTitle={
          <CardMoveHandle
            index={index}
            target={row}
            type={type}
            onMove={onMove}
          />
        }
        slotAfterTitle={<VisibilityToggle />}
      >
        Elevio
      </CollapseCard.Header>
      <CollapseCard.Content>
        <ErrorBoundary
          fallback={<KnowledgeErrorPlaceholder message={errorMessage} />}
        >
          <KnowledgeContent />
        </ErrorBoundary>
      </CollapseCard.Content>
    </CollapseCard>
  );
}
